import axios from 'axios'
import { API_URL } from '@/lib/config'
import { getAuthHeaders, isAuthenticated } from '@/lib/utils'

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Only add auth headers if token is valid
    if (isAuthenticated()) {
      const authHeaders = getAuthHeaders()
      if (authHeaders.headers) {
        config.headers = { ...config.headers, ...authHeaders.headers }
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized
      const currentPath = window.location.pathname

      // Clear auth tokens
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('subscription')

      // Prevent rapid redirects by checking if we just redirected
      const lastRedirect = localStorage.getItem('last_401_redirect')
      const now = Date.now()

      // Only redirect if not already on auth pages and haven't redirected recently
      if (currentPath !== '/login' && currentPath !== '/signup' && currentPath !== '/email/verify') {
        // Check if we redirected in the last 2 seconds to prevent loops
        if (!lastRedirect || (now - parseInt(lastRedirect)) > 2000) {
          localStorage.setItem('last_401_redirect', now.toString())

          // Save current path for redirect after login
          if (currentPath !== '/') {
            localStorage.setItem('ldirect', currentPath)
          }
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

// News API
export const newsApi = {
  // Get news feed
  getFeed: (page = 1, filters = null, curation = null) => {
    const filterParam = filters ? btoa(JSON.stringify(filters)) : null
    const params = { page }
    if (filterParam) params.filters = filterParam
    if (curation) params.curation = curation
    return api.get('/api/v1/news/', { params })
  },

  // Get firehose
  getFirehose: (page = 1, filters = null) => {
    const filterParam = filters ? btoa(JSON.stringify(filters)) : null
    return api.get('/api/v1/news/firehouse/', {
      params: { page, filters: filterParam },
    })
  },

  // Get single article
  getArticle: (slug) => {
    return api.get(`/api/v1/news/${slug}/`)
  },

  // Get top stories (for widget - top 6)
  getTopStories: () => {
    return api.get('/api/v1/news/top-stories/')
  },

  // Get trending news (top stories with pagination)
  getTrending: (page = 1) => {
    return api.get('/api/v1/news/top-stories/', { params: { page } })
  },

  // Get today's news
  getTodayNews: (page = 1) => {
    return api.get('/api/v1/news/todays/', { params: { page } })
  },

  // Get provider details
  getProviderDetails: (slug) => {
    return api.get(`/api/v1/news/providers/${slug}/`)
  },

  // Get news by provider
  getByProvider: (slug, page = 1) => {
    return api.get(`/api/v1/news/providers/${slug}/news/`, { params: { page } })
  },

  // Get tag details
  getTagDetails: (slug) => {
    return api.get(`/api/v1/news/tags/${slug}/`)
  },

  // Get news by tag
  getByTag: (slug, page = 1) => {
    return api.get(`/api/v1/news/tags/${slug}/news/`, { params: { page } })
  },

  // Get bookmarks
  getBookmarks: (page = 1, params = {}) => {
    return api.get('/api/v1/news/book-mark/', { params: { page, ...params } })
  },

  // Toggle bookmark
  toggleBookmark: (slug) => {
    return api.post(`/api/v1/news/${slug}/book-mark/`)
  },

  // Get providers
  getProviders: () => {
    return api.get('/api/v1/news/providers/')
  },

  // Get providers state
  getProvidersState: () => {
    return api.get('/api/v1/news/providers-state/')
  },

  // Get tags
  getTags: () => {
    return api.get('/api/v1/news/tags/')
  },

  // Get next/prev article
  getNextPrev: (slug) => {
    return api.get(`/api/v1/news/next-prev/${slug}/`)
  },

  // Purchase article
  purchaseArticle: (slug) => {
    return api.post(`/api/v1/news/${slug}/purchase/`)
  },

  // Update read time
  updateReadTime: (slug, readTime) => {
    return api.post(`/api/v1/news/${slug}/total-read-time/`, { total_read_time: readTime })
  },

  // Get previous and next articles
  getPrevNext: (slug) => {
    return api.get(`/api/v1/news/next-prev/${slug}/`)
  },

  // Get FAQs
  getFAQs: () => {
    return api.get('/api/v1/news/faqs/')
  },

  // Free tier endpoints
  getDailyStats: () => {
    return api.get('/api/v1/news/my-daily-stats/')
  },

  trackArticleView: (slug) => {
    return api.post(`/api/v1/news/track-view/${slug}/`)
  },

  // Search
  search: (query, page = 1) => {
    return api.get('/api/v1/news/', { params: { search: query, page } })
  },

  // Donations
  getDonations: (params) => {
    return api.get('/api/v1/payments/donations/', { params })
  },

  createDonation: (data) => {
    return api.post('/api/v1/payments/donations/', data)
  },

  updateDonation: (id, data) => {
    return api.patch(`/api/v1/payments/donations/${id}/`, data)
  },

  deleteDonation: (id) => {
    return api.delete(`/api/v1/payments/donations/${id}/`)
  },
}

// Auth API
export const authApi = {
  login: (credentials) => {
    return api.post('/api/v1/token/', credentials)
  },

  signup: (userData) => {
    return api.post('/api/v1/users/', userData)
  },

  verifyEmail: (data) => {
    return api.post('/api/v1/users/verify/', data)
  },

  resendVerification: (userAlias) => {
    return api.post('/api/v1/users/resend-verification/', { user: userAlias })
  },

  checkActivation: (userAlias) => {
    return api.post('/api/v1/users/check-activation/', { user: userAlias })
  },

  logout: () => {
    return api.post('/api/v1/auth/logout/')
  },

  getCurrentUser: () => {
    return api.get('/api/v1/users/me/')
  },

  // Upgrade subscription
  upgradeSubscription: (plan, session) => {
    return api.post('/api/v1/payments/subscription/upgrade/', { plan, session })
  },

  // Cancel subscription
  cancelSubscription: (stripeSubscriptionId) => {
    return api.post('/api/v1/payments/subscription/cancel/', { stripe_subscription_id: stripeSubscriptionId })
  },

  // Password reset
  requestPasswordReset: (email) => {
    return api.post('/api/v1/users/password/reset/request/', { email })
  },

  verifyResetOTP: (email, otp) => {
    return api.post('/api/v1/users/password/reset/verify/', { email, otp })
  },

  resetPassword: (email, otp, new_password) => {
    return api.post('/api/v1/users/password/reset/confirm/', { email, otp, new_password })
  },

  // 2FA Endpoints
  enable2FA: () => {
    return api.post('/api/v1/users/2fa/enable/')
  },

  verify2FASetup: (code) => {
    return api.post('/api/v1/users/2fa/verify-setup/', { code })
  },

  disable2FA: (code) => {
    return api.post('/api/v1/users/2fa/disable/', { code })
  },

  verifyLogin2FA: (tempToken, code) => {
    return api.post('/api/v1/users/2fa/verify-login/', { temp_token: tempToken, code })
  },
}

// Subscription API
export const subscriptionApi = {
  getPlans: () => {
    return api.get('/api/v1/payments/')
  },

  initPayment: (subscriptionId) => {
    return api.post('/api/v1/payments/init/', { subscription_id: subscriptionId })
  },

  verifyPayment: (sessionId) => {
    return api.get(`/api/v1/payments/verify/${sessionId}/`)
  },

  getCurrentSubscription: () => {
    return api.post('/api/v1/payments/subscription/')
  },

  resubscribe: (planId) => {
    return api.post('/api/v1/payments/subscription/resubscribe/', { plan_id: planId })
  },

  // Stripe Customer Portal
  createPortalSession: (returnUrl) => {
    return api.post('/api/v1/payments/subscription/portal/', { return_url: returnUrl })
  },
}

// Dashboard API
export const dashboardApi = {
  // Article Purchases (from old system: /api/v1/news/banks/)
  getArticlePurchases: (page = 1) => {
    return api.get('/api/v1/news/banks/', {
      params: { page }
    })
  },

  // Donations (from old system: /api/v1/payments/donations/)
  getDonations: () => {
    return api.get('/api/v1/payments/donations/')
  },

  // Purchase Transactions (automatic $10+ charges)
  getPurchaseTransactions: (page = 1) => {
    return api.get('/api/v1/payments/transactions/', { params: { page } })
  },

  // Resubscribe (for cancelled subscriptions)
  resubscribe: (planId) => {
    return api.post('/api/v1/payments/subscription/resubscribe/', { plan_id: planId })
  },

  // Legacy endpoint (kept for compatibility)
  getTransactions: (page = 1, pageSize = 10) => {
    return api.get('/api/v1/payments/purchases/transactions/', {
      params: { page, page_size: pageSize }
    })
  },

  // Pending Summary (from old system: /api/v1/payments/my-pending-summary/)
  getPendingSummary: () => {
    return api.get('/api/v1/payments/my-pending-summary/')
  },

  // Payment Methods (placeholder - not in old system, may need backend implementation)
  getPaymentMethods: () => {
    return api.get('/api/v1/payments/methods/')
  },

  addPaymentMethod: (data) => {
    return api.post('/api/v1/payments/methods/', data)
  },

  deletePaymentMethod: (id) => {
    return api.delete(`/api/v1/payments/methods/${id}/`)
  },

  setDefaultPaymentMethod: (id) => {
    return api.post(`/api/v1/payments/methods/${id}/set-default/`)
  },

  // User Profile (using auth endpoint)
  getProfile: () => {
    return authApi.getCurrentUser()
  },

  updateProfile: (data) => {
    return api.patch('/api/v1/users/me/', data)
  },

  changePassword: (data) => {
    return api.post('/api/v1/users/me/update-password/', data)
  },

  deleteAccount: () => {
    return api.delete('/api/v1/users/me/')
  },

  // Notifications (placeholder - may need backend implementation)
  getNotificationSettings: () => {
    return api.get('/api/v1/users/notification-settings/')
  },

  updateNotificationSettings: (data) => {
    return api.patch('/api/v1/users/notification-settings/', data)
  },

  // Activity (placeholder - may need backend implementation)
  getActivity: (page = 1) => {
    return api.get('/api/v1/users/activity/', { params: { page } })
  },

  // Subscribed Feeds (from old system: uses /api/v1/payments/subscription/)
  getSubscribedFeeds: () => {
    return subscriptionApi.getCurrentSubscription()
  },

  // Custom Display Feeds (from old system: /api/v1/payments/custom-display-feeds/)
  // Expects: { choice: [{ alias: 'uuid1' }, { alias: 'uuid2' }] }
  updateDisplayFeeds: (selectedFeeds) => {
    return api.post('/api/v1/payments/custom-display-feeds/', { choice: selectedFeeds })
  },

  // Dashboard Stats (placeholder - may need backend implementation)
  getDashboardStats: () => {
    return api.get('/api/v1/users/dashboard-stats/')
  },
}

// Newsletter API
export const newsletterApi = {
  // Get all newsletters
  list: (params = {}) => {
    return api.get('/api/v1/news/newsletters/', { params })
  },

  // Get details
  get: (slug) => {
    return api.get(`/api/v1/news/newsletters/${slug}/`)
  },

  // Get user subscriptions
  getSubscriptions: (params = {}) => {
    return api.get('/api/v1/news/newsletters/subscriptions/', { params })
  },

  // Subscribe
  subscribe: (slug, type = 'free') => {
    return api.post(`/api/v1/news/newsletters/${slug}/subscribe/`, { subscription_type: type })
  },

  // Unsubscribe
  unsubscribe: (slug) => {
    return api.post(`/api/v1/news/newsletters/${slug}/unsubscribe/`)
  },

  // Get displayed newsletters
  getDisplay: () => {
    return api.get('/api/v1/news/newsletters/display/')
  },

  // Update display settings
  updateDisplay: (slug, data) => {
    return api.patch(`/api/v1/news/newsletters/display/${slug}/`, data)
  }
}

// Curation API
export const curationApi = {
  // Get list of curated users
  getCuratedUsers: () => {
    return api.get('/api/v1/users/curation/list/')
  },

  // Get a curated user's feed list
  getCuratedUserFeeds: (userAlias) => {
    return api.get(`/api/v1/users/curation/${userAlias}/feeds/`)
  },

  // Clone a user's feed (replaces current feed)
  cloneFeed: (sourceUserAlias) => {
    return api.post('/api/v1/users/curation/clone/', { source_user_alias: sourceUserAlias })
  },

  // Add a user's feed (adds to current feed)
  addFeed: (sourceUserAlias) => {
    return api.post('/api/v1/users/curation/add/', { source_user_alias: sourceUserAlias })
  }
}

export default api
